import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ActionBreadcrumbContext } from './plugs/action-breadcrumb';
import type { ActionBreadcrumb } from './plugs/action-breadcrumb';
import { ApiClientContext } from './plugs/api-client';
import type { ApiClient } from './plugs/api-client';
import { CurrentUserContext } from './plugs/current-user';
import type { CurrentUser } from './plugs/current-user';
import { LinkContext } from './plugs/link';
import type { LinkComponent } from './plugs/link';

export interface AppUiProviderProps {
  api: ApiClient;
  link: LinkComponent;
  currentUser: CurrentUser;
  queryClient?: QueryClient;
  onActionBreadcrumb?: ActionBreadcrumb;
  children: ReactNode;
}

const NO_BREADCRUMB: ActionBreadcrumb = () => undefined;

function createDefaultQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  });
}

export function AppUiProvider({
  api,
  link,
  currentUser,
  queryClient,
  onActionBreadcrumb,
  children,
}: AppUiProviderProps) {
  const client = useMemo(() => queryClient ?? createDefaultQueryClient(), [queryClient]);

  return (
    <QueryClientProvider client={client}>
      <ApiClientContext.Provider value={api}>
        <LinkContext.Provider value={link}>
          <CurrentUserContext.Provider value={currentUser}>
            <ActionBreadcrumbContext.Provider value={onActionBreadcrumb ?? NO_BREADCRUMB}>
              {children}
            </ActionBreadcrumbContext.Provider>
          </CurrentUserContext.Provider>
        </LinkContext.Provider>
      </ApiClientContext.Provider>
    </QueryClientProvider>
  );
}
